import { getDay } from 'date-fns/esm'
import { dateUtils } from '@/utils'
import { Dispatch, PropsWithChildren, createContext, useContext, useMemo } from 'react'
import { RecurrenceSettingsAction, RecurrenceWhen } from '../types'
import { useRecurrenceSettings } from '../hooks/useRecurrenceSettings'

const stateContext = createContext<uui.domain.ui.forms.RecurrenceSettings>({
  recurrenceEnd: {
    endType: 'NEVER',
  },
  recurrencePeriod: {
    recurrenceType: 'DAILY',
    date: dateUtils.convertToYYYYMMDD(new Date()),
    period: 1,
  },
})

const dispatchContext = createContext<Dispatch<RecurrenceSettingsAction>>(() => undefined)

type StateProviderProps = PropsWithChildren<{
  fieldValue: uui.domain.ui.forms.RecurrenceSettings
  onChange: (value: uui.domain.ui.forms.RecurrenceSettings) => void
}>

export function StateProvider(props: StateProviderProps) {
  const { fieldValue, onChange, children } = props

  const { state, dispatch } = useRecurrenceSettings(fieldValue, onChange)

  return (
    <stateContext.Provider value={state}>
      <dispatchContext.Provider value={dispatch}>{children}</dispatchContext.Provider>
    </stateContext.Provider>
  )
}

export function useRecurrenceState() {
  return useContext(stateContext)
}

export function useRecurrenceDispatch() {
  return useContext(dispatchContext)
}

export function useRecurrenceType() {
  const { recurrencePeriod } = useRecurrenceState()
  return recurrencePeriod.recurrenceType
}

export function useRecurrenceWhen() {
  const {
    recurrencePeriod: { recurrenceType, cardinal, date, month, weekday },
  } = useRecurrenceState()

  return useMemo<RecurrenceWhen | undefined>(() => {
    if (!date) {
      throw new Error('Recurrence date should be defined')
    }

    switch (recurrenceType) {
      case 'WEEKLY': {
        if (typeof weekday !== 'number') {
          const parsedDate = dateUtils.parseDate(date as uui.domain.YYYYMMDD)
          return {
            type: 'WEEKLY',
            weekday: getDay(parsedDate),
          } as RecurrenceWhen
        }

        return {
          type: 'WEEKLY',
          weekday,
        } as RecurrenceWhen
      }

      case 'MONTHLY': {
        if (typeof cardinal !== 'number' && typeof weekday !== 'number') {
          return {
            type: 'MONTHLY',
            date: date as uui.domain.YYYYMMDD,
          } as RecurrenceWhen
        }

        return {
          type: 'MONTHLY',
          date: date as uui.domain.YYYYMMDD,
          cardinal,
          weekday,
        } as RecurrenceWhen
      }

      case 'ANNUALLY': {
        if (
          typeof cardinal !== 'number' &&
          typeof month !== 'number' &&
          typeof weekday !== 'number'
        ) {
          return {
            type: 'ANNUALLY',
            date: date as uui.domain.YYYYMMDD,
          } as RecurrenceWhen
        }

        return {
          type: 'ANNUALLY',
          date: date as uui.domain.YYYYMMDD,
          cardinal,
          month,
          weekday,
        } as RecurrenceWhen
      }

      case 'NO_REPEAT':
      case 'DAILY': {
        return undefined
      }
    }
  }, [recurrenceType, cardinal, date, month, weekday])
}

export function useRecurrenceDate() {
  const {
    recurrencePeriod: { date, recurrenceType },
  } = useRecurrenceState()

  if (recurrenceType !== 'NO_REPEAT' && !date && process.env.NODE_ENV === 'development') {
    throw new Error('Recurrence date should be defined')
  }

  if (date) return date
  return dateUtils.convertToYYYYMMDD(new Date())
}

export function useRecurrencePeriod() {
  const {
    recurrencePeriod: { period },
  } = useRecurrenceState()

  return period
}

export function useRecurrenceEnd() {
  const { recurrenceEnd } = useRecurrenceState()
  return recurrenceEnd
}
